import { digitsOnly } from './pendentesFormatters';

export function roundPercent(value) {
  return Math.round(Number(value) * 100) / 100;
}

export function formatPercent(value) {
  return new Intl.NumberFormat('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(value || 0));
}

export function normalizeOptionistSearchValue(value) {
  return String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

export function phoneVariants(value) {
  const digits = digitsOnly(value);
  if (!digits) return [];
  const variants = new Set([digits]);
  if (digits.startsWith('55') && digits.length > 11) {
    variants.add(digits.slice(2));
  }
  if (digits.startsWith('0') && digits.length > 10) {
    variants.add(digits.slice(1));
  }
  return [...variants];
}

export function phonesMatch(left, right) {
  const leftVariants = phoneVariants(left);
  const rightVariants = phoneVariants(right);
  if (!leftVariants.length || !rightVariants.length) return false;
  return leftVariants.some(leftDigits =>
    rightVariants.some(
      rightDigits =>
        leftDigits === rightDigits ||
        (leftDigits.length >= 8 && rightDigits.endsWith(leftDigits)) ||
        (rightDigits.length >= 8 && leftDigits.endsWith(rightDigits))
    )
  );
}

export function optionistMatchesPendingItem(optionist, item) {
  if (!optionist || !item) return false;
  if (phonesMatch(optionist.phone, item.optionistPhone)) return true;

  const itemName = normalizeOptionistSearchValue(item.optionistName);
  const optionistName = normalizeOptionistSearchValue(optionist.name);
  if (!itemName || !optionistName) return false;
  return (
    itemName === optionistName ||
    itemName.includes(optionistName) ||
    optionistName.includes(itemName)
  );
}

export function buildAutoAllocationPreview(selectedIds, optionists, marlon) {
  const byId = new Map(
    optionists.map(optionist => [Number(optionist.externalUserId), optionist])
  );
  const nonMarlon = selectedIds
    .map(id => byId.get(Number(id)))
    .filter(optionist => optionist?.canRegister && !optionist.isMarlon);
  if (!marlon?.canRegister || !nonMarlon.length) return [];

  const totalUsers = nonMarlon.length + 1;
  const marlonPercent =
    totalUsers === 2 ? 50 : roundPercent(Math.max(25, 100 / totalUsers));
  const remaining = 100 - marlonPercent;
  const basePercent = roundPercent(remaining / nonMarlon.length);
  const nonMarlonRows = nonMarlon.map((optionist, index) => ({
    optionist,
    position: index === 0 ? 1 : index + 2,
    percentage: basePercent,
  }));
  const diff =
    100 -
    marlonPercent -
    nonMarlonRows.reduce((sum, row) => sum + row.percentage, 0);
  nonMarlonRows[0].percentage = roundPercent(
    nonMarlonRows[0].percentage + diff
  );
  return [
    nonMarlonRows[0],
    { optionist: marlon, position: 2, percentage: marlonPercent },
    ...nonMarlonRows.slice(1),
  ].map((row, index) => ({ ...row, position: index + 1 }));
}

export function buildAllocationPreview(
  selectedIds,
  optionists,
  marlon,
  manualPercentages = {}
) {
  const rows = buildAutoAllocationPreview(selectedIds, optionists, marlon);
  const nonMarlonRows = rows.filter(row => !row.optionist.isMarlon);
  if (nonMarlonRows.length <= 1) return rows;

  const editedRows = rows.map(row => {
    if (row.optionist.isMarlon) return row;
    const manualValue = manualPercentages[String(row.optionist.externalUserId)];
    const percentage = Number(manualValue);
    return {
      ...row,
      percentage: Number.isFinite(percentage)
        ? roundPercent(percentage)
        : row.percentage,
    };
  });
  const nonMarlonTotal = editedRows
    .filter(row => !row.optionist.isMarlon)
    .reduce((sum, row) => sum + Number(row.percentage || 0), 0);
  const marlonPercentage = roundPercent(100 - nonMarlonTotal);
  return editedRows.map(row =>
    row.optionist.isMarlon ? { ...row, percentage: marlonPercentage } : row
  );
}

export function allocationErrorsFor(rows) {
  const nonMarlonRows = rows.filter(row => !row.optionist.isMarlon);
  const errors = [];
  if (!rows.length) return errors;
  nonMarlonRows.forEach(row => {
    if (
      !Number.isFinite(Number(row.percentage)) ||
      Number(row.percentage) <= 0
    ) {
      errors.push(`Informe a % de ${row.optionist.name}.`);
    }
  });
  const marlonRow = rows.find(row => row.optionist.isMarlon);
  if (marlonRow && Number(marlonRow.percentage) < 25) {
    errors.push('Marlon Reis deve ficar com no mínimo 25%.');
  }
  const total = rows.reduce((sum, row) => sum + Number(row.percentage || 0), 0);
  if (Math.abs(total - 100) > 0.05) {
    errors.push('A soma dos percentuais precisa fechar em 100%.');
  }
  return [...new Set(errors)];
}
